import React, { useState, useEffect } from 'react';
import { Box, useTheme, useMediaQuery, Typography, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Stack, Popover, Divider } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, ReportProblemOutlined as IncidentIcon, WarningAmberOutlined as WarningIcon, HelpOutline as SupportIcon, ConfirmationNumberOutlined as TicketIcon, ChatBubbleOutline as MessageIcon, SupportAgent as SupportAgentIcon, Email as EmailIcon, Person as PersonIcon, WhatsApp as WhatsAppIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'shared/hooks/queryConfig';
import { walkthroughApi } from 'shared/services/walkthroughApi';
import { Sidebar, PendingVerificationsPopup, HelpCenter, RoleInfoModal } from 'shared/components'
import { GlobalLoader } from 'shared/components/layout/GlobalLoader';
import { ProfileDrawer } from 'shared/components/layout/ProfileDrawer';
import { IncidentReportModal } from 'shared/components/IncidentReportModal';
import { AttentionCallModal } from 'shared/components/AttentionCallModal';
import { SupportTicketModal } from 'shared/components/SupportTicketModal';
import { NotificationBell } from './NotificationBell';
import OverCapacityBanner from 'shared/components/OverCapacityBanner';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { useCompany } from 'shared/hooks/queries/useCompanies';
import { useGlobalLoader } from 'shared/contexts/GlobalLoaderContext';
import { usePageTitleContext } from 'shared/contexts/PageTitleContext';
import { useCompanyThemeLoader } from 'shared/hooks/useCompanyThemeLoader';
import { useRestrictedProviderAccess } from 'shared/hooks/queries/useRestrictedProviderAccess';
import { RestrictedProviderAccessScreen } from 'shared/components/RestrictedProviderAccessScreen';
import { useProviderPlanBlock } from 'shared/hooks/queries/useProviderPlanBlock';
import { ProviderPlanBlockedScreen } from 'shared/components/ProviderPlanBlockedScreen';
import { getHomeProviderAdminCompany } from 'shared/utils/roleUtils';

export const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery('(max-width:1400px)');
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [roleInfoOpen, setRoleInfoOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Force collapsed sidebar below 1400px
  const effectiveCollapsed = isNarrow || sidebarCollapsed;
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [attentionCallModalOpen, setAttentionCallModalOpen] = useState(false);
  const [supportTicketModalOpen, setSupportTicketModalOpen] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(null);
  const [monitorAnchor, setMonitorAnchor] = useState<null | HTMLElement>(null);

  const { user, userAvatarUrl } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const restrictedAccess = useRestrictedProviderAccess();
  const providerPlanBlock = useProviderPlanBlock();

  // v3 first-payment activation gate: company_admins whose subscription is
  // pending_first_payment / suspended_unpaid get redirected to /walkthrough.
  // Single one-shot check on mount + when role/location changes. Failures are
  // silent — the BE middleware (when mounted) is the actual gate; this is UX.
  useEffect(() => {
    if (user?.role !== 'company_admin') return;
    if (location.pathname.startsWith('/walkthrough')) return;
    walkthroughApi
      .needs()
      .then((r) => {
        if (r.needs) navigate('/walkthrough', { replace: true });
      })
      .catch(() => {
        /* never block the UI on a billing-check hiccup */
      });
  }, [user?.role, location.pathname, navigate]);
  // Preserve the company context for the Mensajes shortcut — if the
  // user is currently inside /companies/:id/..., navigate to that
  // company's scoped Mensajes route; otherwise the global one. Layout
  // is mounted above the route tree so useParams is empty here; parse
  // the company id off the pathname directly.
  const companyIdMatch = location.pathname.match(/^\/companies\/([0-9a-fA-F]{24})(?:\/|$)/);
  const scopedCompanyId = companyIdMatch?.[1];
  const mensajesHref = scopedCompanyId
    ? `/companies/${scopedCompanyId}/mensajes`
    : '/mensajes';

  // Resolve companyId from URL or user context for incident reports
  const companyMatch = location.pathname.match(/^\/companies\/([^/]+)/);
  const urlCompanyId = companyMatch && !location.pathname.includes('/companies/new') && !location.pathname.includes('/companies/unlinked-users')
    ? companyMatch[1] : null;
  const incidentCompanyId = urlCompanyId || user?.company?._id || undefined;
  const isPlatformRole = user?.role === 'super_admin' || user?.role === 'safety_staff';
  const showIncidentButton = isPlatformRole ? !!urlCompanyId : !!user?.company?._id;
  const { isLoading } = useGlobalLoader();
  const { pageTitle, titleExtra } = usePageTitleContext();

  // Load company theme based on current context (URL or user's company)
  useCompanyThemeLoader();

  // Active-company chip — for non-platform roles this is user's own
  // company; for platform users (super_admin / safety_staff) browsing
  // a /companies/:id/* page, surface the company being viewed instead.
  const isPlatformBrowsing = isPlatformRole && !!scopedCompanyId;
  const { data: viewedCompany } = useCompany(scopedCompanyId || '', isPlatformBrowsing);
  // Suppress the chip on the company "resumen" page — that page already
  // shows the company name prominently in its own header, so the chip
  // would be redundant.
  const isCompanyResumenPage = /^\/companies\/[0-9a-fA-F]{24}\/resumen\/?$/.test(location.pathname);
  const activeCompanyName = isCompanyResumenPage
    ? undefined
    : isPlatformBrowsing
      ? viewedCompany?.data?.name
      : user?.company?.name;

  // Soporte Monitor contact — surface the assigned account executives
  // (titular/suplente) as a header popover whenever we have a company
  // context:
  //  - client_staff / client_approver / security / company_admin →
  //    own company (from AuthContext, refetched for accountExecutive).
  //  - super_admin / safety_staff → only when browsing /companies/:id
  //    (uses the viewedCompany fetched above).
  // AuthContext's cached `company` object does NOT carry
  // accountExecutive, so we always re-fetch via useCompany.
  const ownCompanyRoles: string[] = [
    UserRole.CLIENT_STAFF,
    UserRole.CLIENT_APPROVER,
    UserRole.SECURITY,
    UserRole.COMPANY_ADMIN,
  ];
  const userCompanyId = user?.company?._id;
  const shouldFetchOwnCompany =
    !!user?.role && ownCompanyRoles.includes(user.role) && !!userCompanyId;
  const { data: ownCompanyRes } = useCompany(userCompanyId || '', shouldFetchOwnCompany);
  const fullCompany = isPlatformBrowsing ? viewedCompany?.data : ownCompanyRes?.data;
  const accountExec = fullCompany?.accountExecutive;
  const monitorTitular =
    accountExec?.titular && typeof accountExec.titular === 'object' && accountExec.titular.firstName
      ? accountExec.titular : null;
  const monitorSuplente =
    accountExec?.suplente && typeof accountExec.suplente === 'object' && accountExec.suplente.firstName
      ? accountExec.suplente : null;
  const hasCompanyContext =
    (!!user?.role && ownCompanyRoles.includes(user.role) && !!userCompanyId) ||
    isPlatformBrowsing;
  const showMonitorButton =
    hasCompanyContext && (!!monitorTitular || !!monitorSuplente);

  const waUrl = (phone: string) => `https://wa.me/${phone.replace(/\D/g, '')}`;
  const goToMessages = (recipientId: string) => {
    setMonitorAnchor(null);
    navigate(`${mensajesHref}?to=${recipientId}`);
  };

  // This user's own provider company, if they're its (active, non-external)
  // company_admin — regardless of which company is currently active. Only
  // relevant when they're not already showing as company_admin: someone
  // lent out to a client as client_staff still runs their own provider
  // company, and "Colaborador" alone hides that.
  const homeProviderAdmin =
    user?.role !== UserRole.COMPANY_ADMIN ? getHomeProviderAdminCompany(user) : null;

  const getRoleLabel = (role: string | undefined, fullName: boolean = false) => {
    if (homeProviderAdmin) return 'Admin Proveedor';
    if (!role) return 'Usuario';

    // Labels match the canonical UI nomenclature in CLAUDE.md. Don't conflate
    // role with the `isExternal` flag (those are independent axes — see the
    // multi-tenant `companies[].isExternal` field).
    const roleLabels: Record<string, { short: string; full: string }> = {
      'super_admin': { short: 'Super Admin', full: 'Super Admin' },
      'safety_staff': { short: 'Soporte', full: 'Soporte Monitor' },
      'company_admin': { short: 'Administrador', full: 'Administrador de Empresa' },
      'client_approver': { short: 'Aprobador', full: 'Aprobador' },
      'client_staff': { short: 'Colaborador', full: 'Colaborador' },
      'security': { short: 'Seguridad', full: 'Seguridad' },
    };

    const label = roleLabels[role.toLowerCase()];
    if (!label) return role;

    return fullName ? label.full : label.short;
  };

  if (restrictedAccess.restricted && restrictedAccess.relationshipId) {
    return (
      <RestrictedProviderAccessScreen
        relationshipId={restrictedAccess.relationshipId}
        companyName={activeCompanyName}
      />
    );
  }

  if (providerPlanBlock.blocked) {
    return <ProviderPlanBlockedScreen companyName={providerPlanBlock.companyName} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        ...(isMobile && {
          flexDirection: 'column',
          minHeight: '100vh',
        }),
        ...(!isMobile && {
          height: '100vh',
        }),
      }}
    >
      {/* Sidebar - Same component, responsive behavior handled internally */}
      <Sidebar
        collapsed={effectiveCollapsed}
        onToggleCollapse={isNarrow ? undefined : () => setSidebarCollapsed(!sidebarCollapsed)}
        onOpenProfile={() => setProfileDrawerOpen(prev => !prev)}
        profileOpen={profileDrawerOpen}
        onCloseProfile={() => setProfileDrawerOpen(false)}
        onOpenIncident={showIncidentButton ? () => setIncidentModalOpen(true) : undefined}
        onOpenAttentionCall={showIncidentButton ? () => setAttentionCallModalOpen(true) : undefined}
        onOpenSupportTicket={() => setSupportTicketModalOpen(true)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          width: '100%',
          position: 'relative',
          bgcolor: (theme) => theme.palette.background.default,
        }}
      >
        <GlobalLoader open={isLoading} />

        {/* Top header bar — desktop only */}
        {!isMobile && user && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3.5,
            pt: '40px',
            pb: 1.5,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            {/* Page title — titleExtra (overflow menu, chips, etc.)
                renders inline next to the title, not stacked beneath.
                Active company chip surfaces on every page (not just
                /my-company/*) so admins always know which company
                they're scoped to. */}
            {(pageTitle || activeCompanyName) && (
              <Box sx={{ pl: '24px', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                {pageTitle && (
                  <Typography
                    variant="h4"
                    noWrap
                    sx={{
                      fontWeight: 700,
                      fontSize: '2rem',
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    {pageTitle}
                  </Typography>
                )}
                {titleExtra && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {titleExtra}
                  </Box>
                )}
                {activeCompanyName && (
                  <Tooltip title={isPlatformBrowsing ? 'Empresa que está viendo' : 'Empresa activa'} arrow>
                    <Chip
                      label={activeCompanyName}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ height: 24, fontWeight: 500, ml: 0.5 }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Report button + profile section. Order: Reportar (ticket) → bell. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {/* Single report button that opens a menu */}
              <Box
                role="button"
                tabIndex={0}
                aria-label="Reportar"
                onClick={(e) => setReportMenuAnchor(e.currentTarget as HTMLElement)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setReportMenuAnchor(e.currentTarget as HTMLElement); } }}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: 30,
                  borderRadius: '15px',
                  px: 0.5,
                  overflow: 'visible',
                  '&:hover .report-label': {
                    maxWidth: 120,
                    opacity: 1,
                    mr: 0.5,
                  },
                }}
              >
                <Typography
                  className="report-label"
                  variant="body2"
                  noWrap
                  sx={{
                    maxWidth: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    transition: 'max-width 0.3s ease, opacity 0.3s ease, margin 0.3s ease',
                    color: 'warning.main',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    mr: 0,
                  }}
                >
                  Reportar
                </Typography>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: '1.5px solid',
                      borderColor: 'warning.light',
                      opacity: 0,
                      '@keyframes ripple': {
                        '0%': { transform: 'scale(1)', opacity: 0.4 },
                        '100%': { transform: 'scale(1.8)', opacity: 0 },
                      },
                      animation: 'ripple 3s ease-out infinite',
                    },
                  }} />
                  <IconButton
                    size="small"
                    aria-label="Reportar incidente"
                    tabIndex={-1}
                    sx={{
                      color: 'warning.main',
                      width: 28,
                      height: 28,
                      zIndex: 1,
                      '&:hover': {
                        bgcolor: 'warning.main',
                        color: 'background.buttonText',
                      },
                    }}
                  >
                    <TicketIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Report type dropdown menu */}
              <Menu
                anchorEl={reportMenuAnchor}
                open={Boolean(reportMenuAnchor)}
                onClose={() => setReportMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {showIncidentButton && (
                  <MenuItem onClick={() => { setReportMenuAnchor(null); setIncidentModalOpen(true); }}>
                    <ListItemIcon><IncidentIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Reportar Accidente</ListItemText>
                  </MenuItem>
                )}
                {showIncidentButton && (
                  <MenuItem onClick={() => { setReportMenuAnchor(null); setAttentionCallModalOpen(true); }}>
                    <ListItemIcon><WarningIcon fontSize="small" color="warning" /></ListItemIcon>
                    <ListItemText>Llamado de Atención</ListItemText>
                  </MenuItem>
                )}
                <MenuItem onClick={() => { setReportMenuAnchor(null); setSupportTicketModalOpen(true); }}>
                  <ListItemIcon><SupportIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>Ticket de Soporte</ListItemText>
                </MenuItem>
              </Menu>

              {/* Soporte Monitor contact — popover with titular/suplente
                  contact info. Visible for all non-admin company roles
                  (client_staff / client_approver / security). The
                  hover-reveal label mirrors the Reportar affordance so
                  the entry point reads as "a person you can contact",
                  not just an ambiguous icon. */}
              {showMonitorButton && (
                <>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Contactar Soporte Monitor"
                    onClick={(e) => setMonitorAnchor(e.currentTarget as HTMLElement)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMonitorAnchor(e.currentTarget as HTMLElement); } }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      height: 30,
                      borderRadius: '15px',
                      px: 0.5,
                      '&:hover .monitor-label': {
                        maxWidth: 140,
                        opacity: 1,
                        mr: 0.5,
                      },
                    }}
                  >
                    <Typography
                      className="monitor-label"
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: 0,
                        opacity: 0,
                        overflow: 'hidden',
                        transition: 'max-width 0.3s ease, opacity 0.3s ease, margin 0.3s ease',
                        color: 'primary.main',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        mr: 0,
                      }}
                    >
                      Soporte Monitor
                    </Typography>
                    <Box sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}>
                      <PersonIcon sx={{ fontSize: 18 }} />
                      <SupportAgentIcon
                        sx={{
                          fontSize: 12,
                          position: 'absolute',
                          bottom: -2,
                          right: -2,
                          bgcolor: 'background.paper',
                          color: 'primary.main',
                          borderRadius: '50%',
                          p: '1px',
                        }}
                      />
                    </Box>
                  </Box>
                  <Popover
                    open={Boolean(monitorAnchor)}
                    anchorEl={monitorAnchor}
                    onClose={() => setMonitorAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { p: 2, minWidth: 260, maxWidth: 320 } } }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <SupportAgentIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Soporte Monitor
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Contacto de Safety a cargo de esta empresa.
                    </Typography>
                    <Stack spacing={1.5}>
                      {monitorTitular && (
                        <Box>
                          <Chip label="Titular" size="small" variant="outlined" sx={{ mb: 0.5, fontSize: '0.65rem', height: 18 }} />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {monitorTitular.firstName} {monitorTitular.lastName}
                            </Typography>
                          </Stack>
                          {monitorTitular.email && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                <a href={`mailto:${monitorTitular.email}`}>{monitorTitular.email}</a>
                              </Typography>
                            </Stack>
                          )}
                          {monitorTitular.phone && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{monitorTitular.phone}</Typography>
                            </Stack>
                          )}
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                            <Tooltip title="Enviar mensaje" arrow>
                              <IconButton
                                size="small"
                                onClick={() => goToMessages(monitorTitular._id)}
                                sx={{ color: 'primary.main' }}
                              >
                                <MessageIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {monitorTitular.phone && (
                              <Tooltip title="WhatsApp" arrow>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={waUrl(monitorTitular.phone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: 'brand.whatsapp' }}
                                >
                                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      )}
                      {monitorTitular && monitorSuplente && <Divider />}
                      {monitorSuplente && (
                        <Box>
                          <Chip label="Suplente" size="small" variant="outlined" sx={{ mb: 0.5, fontSize: '0.65rem', height: 18 }} />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {monitorSuplente.firstName} {monitorSuplente.lastName}
                            </Typography>
                          </Stack>
                          {monitorSuplente.email && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                <a href={`mailto:${monitorSuplente.email}`}>{monitorSuplente.email}</a>
                              </Typography>
                            </Stack>
                          )}
                          {monitorSuplente.phone && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{monitorSuplente.phone}</Typography>
                            </Stack>
                          )}
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                            <Tooltip title="Enviar mensaje" arrow>
                              <IconButton
                                size="small"
                                onClick={() => goToMessages(monitorSuplente._id)}
                                sx={{ color: 'primary.main' }}
                              >
                                <MessageIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {monitorSuplente.phone && (
                              <Tooltip title="WhatsApp" arrow>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={waUrl(monitorSuplente.phone)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: 'brand.whatsapp' }}
                                >
                                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Popover>
                </>
              )}

              {/* Mensajes shortcut — quick jump from anywhere in the
                  app. Preserves company context when applicable
                  (see scopedCompanyId above). */}
              <Tooltip title="Mensajes" arrow>
                <IconButton
                  size="small"
                  aria-label="Mensajes"
                  onClick={() => navigate(mensajesHref)}
                  sx={{
                    color: 'text.secondary',
                    width: 32,
                    height: 32,
                  }}
                >
                  <MessageIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>

              {/* Notification bell — sits after the Reportar button per the
                  reorder request so the ticket icon is the leftmost. */}
              <NotificationBell />

            {/* User name + avatar — opens profile drawer */}
            <Box
              role="button"
              tabIndex={0}
              aria-label="Abrir perfil"
              onClick={() => setProfileDrawerOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setProfileDrawerOpen(true); } }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                borderRadius: '8px',
                px: 1,
                py: 0.5,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: (theme) => theme.palette.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {user.name || '??'}
                </Typography>
                <Chip
                  label={getRoleLabel(user?.role)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', mt: 0.25, cursor: 'pointer' }}
                />
              </Box>
              {userAvatarUrl ? (
                <Box
                  component="img"
                  src={userAvatarUrl}
                  alt={user.name}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: (theme) => theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {(user.name || '??').split(' ').map((w: string) => w.charAt(0)).join('').toUpperCase().slice(0, 2)}
                </Box>
              )}
              {profileDrawerOpen ? (
                <KeyboardArrowUp sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </Box>
            </Box>
          </Box>
        )}

        <Box sx={{
          px: { xs: 2, md: 3.5 },
          pt: { xs: 1.5, md: 1.5 },
          pb: { xs: 2, md: 3.5 },
          // On desktop, hide page-level titles (shown in header bar instead)
          ...(!isMobile && pageTitle ? {
            '& > div > h4:first-of-type': { display: 'none' },
            '& > div > h5:first-of-type': { display: 'none' },
            '& > div > div:first-of-type > h4:first-of-type': { display: 'none' },
            '& > div > div:first-of-type > h5:first-of-type': { display: 'none' },
            '& > div > div:first-of-type > div:first-of-type > h4': { display: 'none' },
            '& > div > div:first-of-type > div:first-of-type > h5': { display: 'none' },
            '& > div > div:first-of-type': { justifyContent: 'flex-start !important' },
          } : {}),
        }}>
          <OverCapacityBanner
            companyId={isPlatformRole ? scopedCompanyId || null : user?.company?._id || null}
          />
          <Outlet />
        </Box>
      </Box>

      {/* Modals and Popups */}
      <Box sx={{ zIndex: theme.zIndex.drawer - 1 }}>
        <HelpCenter
          open={helpCenterOpen}
          onClose={() => setHelpCenterOpen(false)}
        />
      </Box>

      <RoleInfoModal
        open={roleInfoOpen}
        onClose={() => setRoleInfoOpen(false)}
        role={user?.role}
        roleName={getRoleLabel(user?.role, true)}
      />

      <PendingVerificationsPopup />

      <IncidentReportModal
        open={incidentModalOpen}
        onClose={() => {
          setIncidentModalOpen(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.incidentReports.all });
        }}
        companyId={incidentCompanyId}
      />

      <AttentionCallModal
        open={attentionCallModalOpen}
        onClose={() => {
          setAttentionCallModalOpen(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.attentionCalls.all });
        }}
        companyId={incidentCompanyId}
      />

      <SupportTicketModal
        open={supportTicketModalOpen}
        onClose={() => {
          setSupportTicketModalOpen(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all });
        }}
        companyId={incidentCompanyId}
      />

      <ProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
      />
    </Box>
  );
};

export default Layout;